'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { getUserRole } from '@/lib/remote-config';

export type UserRole = 'operatore' | 'gestore' | 'admin';

interface RoleContextType {
  userRole: UserRole | null;
  isAdmin: boolean;
  isGestore: boolean;
  isOperatore: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canCreate: boolean;
  isLoading: boolean;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (user?.email) {
        setIsLoading(true);
        try {
          // Recupera il ruolo da Firebase Remote Config
          const role = await getUserRole(user.email);
          setUserRole(role);
        } catch (error) {
          console.error('[RoleContext] Errore nel recupero ruolo:', error);
          setUserRole(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        setUserRole(null);
        setIsLoading(false);
      }
    };

    fetchUserRole();
  }, [user]);

  const isAdmin = userRole === 'admin';
  const isGestore = userRole === 'gestore';
  const isOperatore = userRole === 'operatore';

  // Permessi
  const canEdit = isAdmin || isGestore;
  const canDelete = isAdmin || isGestore;
  const canCreate = isAdmin || isGestore;

  return (
    <RoleContext.Provider
      value={{
        userRole,
        isAdmin,
        isGestore,
        isOperatore,
        canEdit,
        canDelete,
        canCreate,
        isLoading,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}

// Hook per proteggere componenti che richiedono privilegi admin
export function useRequireAdmin() {
  const { isAdmin } = useRole();
  return isAdmin;
}

// Componente per mostrare contenuti solo agli admin
export function AdminOnly({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useRole();
  
  if (!isAdmin) {
    return null;
  }
  
  return <>{children}</>;
}

// Componente per mostrare contenuti solo ai gestori (admin + gestore)
export function GestoreOnly({ children }: { children: React.ReactNode }) {
  const { isAdmin, isGestore } = useRole();

  if (!isAdmin && !isGestore) {
    return null;
  }

  return <>{children}</>;
}

// Componente per mostrare contenuti solo agli operatori
export function OperatoreOnly({ children }: { children: React.ReactNode }) {
  const { isOperatore } = useRole();

  if (!isOperatore) {
    return null;
  }

  return <>{children}</>;
}

// Componente per mostrare messaggi di accesso negato
export function AccessDenied({ action = 'questa azione' }: { action?: string }) {
  return (
    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div className="flex items-center gap-2">
        <div className="text-yellow-600">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div>
          <h3 className="text-sm font-medium text-yellow-800">
            Accesso Limitato
          </h3>
          <p className="text-sm text-yellow-700 mt-1">
            Non hai i permessi necessari per {action}. Contatta un amministratore per ricevere i privilegi necessari.
          </p>
        </div>
      </div>
    </div>
  );
}